import React from 'react';
import { 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Box, 
  Button, 
  Paper,
  Avatar,
  alpha,
  useTheme,
  Grid,
  Stack,
  CardActions
} from '@mui/material';
import { 
  Person as PersonIcon, 
  Group as GroupIcon, 
  Event as EventIcon,
  School as SchoolIcon,
  PersonPin as PersonPinIcon,
  VerifiedUser as VerifiedUserIcon,
  SupportAgent as SupportAgentIcon,
  NavigateNext as NavigateNextIcon // For the button
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import PageHeader from '../components/PageHeader';

const MuiIcons: { [key: string]: React.ElementType } = {
  PersonPin: PersonPinIcon,
  VerifiedUser: VerifiedUserIcon,
  SupportAgent: SupportAgentIcon,
};

interface ServiceItem {
  title: string;
  summary: string;
  image: string;
  icon: React.ReactNode;
  path: string;
}

// Define valid keys for service translations locally
type ServiceTranslationKey = 'individueleCoaching' | 'workshops' | 'lesgeven';

// Removed StyledServiceCard, ServiceIconBox, ServiceAvatar as we'll use standard Cards

const services: ServiceItem[] = [
  {
    title: "Individuele Coaching",
    summary: "Persoonlijke begeleiding om jouw unieke talenten te ontdekken en maximaal te benutten in je loopbaan.",
    image: "/images/individual-coaching.jpg",
    icon: <PersonIcon color="inherit" sx={{ fontSize: 28 }} />,
    path: "/diensten/individuele-coaching"
  },
  {
    title: "Workshops & Groepstrainingen",
    summary: "Inspirerende bijeenkomsten en interactieve sessies voor persoonlijke en professionele groei.",
    image: "/images/workshops.jpg",
    icon: <EventIcon color="inherit" sx={{ fontSize: 28 }} />,
    path: "/diensten/workshops"
  },
  {
    title: "Lesgeven & Public Speaking",
    summary: "Professionele kennisoverdracht en inspirerende presentaties voor diverse doelgroepen.",
    image: "/images/teaching.jpg",
    icon: <SchoolIcon color="inherit" sx={{ fontSize: 28 }} />,
    path: "/diensten/lesgeven"
  }
];

const Diensten = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language].diensten;
  const theme = useTheme();

  // Simplified translation fetching using keys
  const getTranslatedTitle = (titleKey: string) => {
    const keyMap: { [key: string]: ServiceTranslationKey } = { // Use local type
        "Individuele Coaching": "individueleCoaching",
        "Workshops & Groepstrainingen": "workshops",
        "Lesgeven & Public Speaking": "lesgeven"
    };
    const serviceKey: ServiceTranslationKey | undefined = keyMap[titleKey]; // Type serviceKey
    // Check if key exists before accessing
    return serviceKey ? translations[language].services[serviceKey] : titleKey;
  };

  const getTranslatedSummary = (title: string, summary: string) => {
    if (language === 'nl') return summary;
    
    switch (title) {
      case "Individuele Coaching": 
        return "Personal guidance to discover your unique talents and make the most of them in your career.";
      case "Workshops & Groepstrainingen": 
        return "Inspiring gatherings and interactive sessions for personal and professional growth.";
      case "Lesgeven & Public Speaking": 
        return "Professional knowledge transfer and inspiring presentations for various target groups.";
      default: return summary;
    }
  };
  
  return (
    <>
      <PageHeader
        title={t.title}
        subtitle={t.subtitle}
      />

      <Box sx={{ bgcolor: 'background.default', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
            {t.introTitle}
          </Typography>
          <Typography variant="body1" align="center" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: 'text.secondary', mb: t.introParagraph2 ? 2 : 0 }}>
            {t.introParagraph1}
          </Typography>
          {t.introParagraph2 && (
             <Typography variant="body1" align="center" sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: 'text.secondary' }}>
               {t.introParagraph2}
             </Typography>
          )}
        </Container>
      </Box>

      <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ fontWeight: 500, mb: 6 }}>
            {t.approachTitle}
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {t.approachItems.map((item, index) => {
               const IconComponent = MuiIcons[item.icon] || PersonPinIcon;
               return (
                <Grid item xs={12} sm={6} md={4} key={index} sx={{ textAlign: 'center' }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 60, height: 60, mb: 2, mx: 'auto' }}>
                    <IconComponent sx={{ color: 'white' }} />
                  </Avatar>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                </Grid>
               );
             })}
          </Grid>
        </Container>
      </Box>

      {/* Services Section - Refactored with Card UI */}
      <Box sx={{ bgcolor: 'background.default', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography 
              variant="h3" 
              component="h2" 
              gutterBottom 
              sx={{ 
                fontWeight: 500, 
                mb: 2 
              }}
            >
              {t.servicesTitle}
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                maxWidth: '700px', 
                mx: 'auto',
                color: 'text.secondary'
              }}
            >
              {t.servicesDescription}
            </Typography>
          </Box>
          
          <Grid container spacing={4} justifyContent="center">
            {services.map((service, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  boxShadow: 3,
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6
                  }
                }}>
                  <Box sx={{ 
                    bgcolor: theme.palette.primary.light, 
                    p: 3, 
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 110 // Ensure consistent header height
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'white' }}>
                      {/* Wrap icon in Box to apply margin safely */}
                      {service.icon && React.isValidElement(service.icon) ? (
                        <Box component="span" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}> 
                          {service.icon}
                        </Box>
                      ) : null}
                      <Typography variant="h5" component="h3" fontWeight={600} color="inherit">
                        {getTranslatedTitle(service.title)}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <CardContent sx={{ flexGrow: 1, p: 2, pt: 3, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, flexGrow: 1 }}>
                       {getTranslatedSummary(service.title, service.summary)}
                    </Typography>
                  </CardContent>
                  
                  <CardActions sx={{ p: 2, pt: 0, mt: 'auto', justifyContent: 'center' }}>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      color="secondary"
                      endIcon={<NavigateNextIcon />}
                      onClick={() => navigate(service.path)}
                    >
                      {t.moreInfoButton || (language === 'nl' ? 'Meer Informatie' : 'More Info')}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
            {t.cta.title}
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: 'text.secondary', mb: 4 }}>
            {t.cta.description}
          </Typography>
          <Button 
            variant="contained" 
            color="secondary"
            size="large"
            component={RouterLink}
            to="/contact"
          >
            {t.cta.contactButton}
          </Button>
        </Container>
      </Box>
    </>
  );
};

export default Diensten; 